import {
  AccountsPayable,
  costTypeMapper,
  frequencyTypeMapper
} from 'models/AccountsPayable'
import { v4 } from 'uuid'

import { NewExpenseSchema } from './schema'

const frequencyMapper: {
  [key: number]: { frequency_type: number; frequency_number: number }
} = {
  0: { frequency_type: 2, frequency_number: 1 },
  1: { frequency_type: 3, frequency_number: 1 },
  2: { frequency_type: 3, frequency_number: 3 },
  3: { frequency_type: 3, frequency_number: 6 },
  4: { frequency_type: 4, frequency_number: 1 }
}

const getFrequencyFromOutside = (
  frequencyType: string,
  number: number
): number | undefined => {
  const type =
    frequencyTypeMapper[frequencyType as keyof typeof frequencyTypeMapper]
  return Object.entries(frequencyMapper).find(
    ([, value]) =>
      value.frequency_type === type && value.frequency_number === number
  )?.[0] as unknown as number | 1
}

/**
 * Valor unitário do item para o campo 'Valor' do form. `amountBilled` é o total
 * da linha (unitário × quantidade): carregá-lo no campo unitário fazia a tela
 * recalcular o total multiplicando de novo. Usa `amountUnit` da API; se não
 * vier, divide o total pela quantidade, arredondando em centavos.
 */
export const itemUnitPrice = (item: {
  amountUnit?: number
  amountBilled: number
  quantity: number
}): number => {
  if (Number.isFinite(item.amountUnit)) return item.amountUnit as number
  if (!item.quantity) return item.amountBilled
  return Math.round((item.amountBilled / item.quantity) * 100) / 100
}

export const mapExpenseToDuplicateFormValues = (
  expense: AccountsPayable
): Partial<NewExpenseSchema> => {
  const mappedCostType = (costTypeMapper as any)[expense.costType]
  const costType =
    mappedCostType === 3 ? (expense.totalCycles > 1 ? 2 : 1) : mappedCostType
  const accountsPayableFrequency = mappedCostType === 3 ? 1 : 0

  const totalCyclesValue =
    expense.totalCycles === 0
      ? 0
      : expense.totalCycles > 1
      ? expense.totalCycles >= 13
        ? 13
        : expense.totalCycles
      : 12

  const frequency =
    expense.frequencyNumber && expense.frequencyType
      ? getFrequencyFromOutside(expense.frequencyType, expense.frequencyNumber)
      : 1

  const repeat =
    !!expense.frequencyNumber &&
    !!expense.frequencyType &&
    expense.totalCycles !== 1

  const dueDate = expense.dueDate ? new Date(expense.dueDate) : undefined
  const competence = expense.competenceDate
    ? new Date(expense.competenceDate)
    : undefined

  return {
    allocations:
      expense.allocations?.map((allocation) => ({
        uuid: v4(),
        category: {
          value: allocation.category.uuid,
          label: allocation.category.name
        },
        costCenter: {
          value: allocation.costCenter.uuid,
          label: allocation.costCenter.name
        },
        amount: allocation.amount,
        percentage: allocation.percentage
      })) || [],
    enableRateio: (expense.allocations?.length ?? 0) > 1,
    // Mesmo motivo do reset em ModalNewExpense: o payee é opcional na despesa.
    payee: expense.payee
      ? { label: expense.payee.name, value: expense.payee.uuid }
      : { label: '', value: '' },
    description: expense.description || '',
    dueDate,
    competence,
    source: expense.source
      ? { label: expense.source.name, value: expense.source.uuid }
      : undefined,
    expenses:
      expense.items?.map((item) => ({
        uuid: v4(),
        description: item.description,
        price: itemUnitPrice(item),
        quantity: item.quantity,
        total: item.amountBilled
      })) || [],
    costType,
    accountsPayableFrequency,
    repeat,
    frequency,
    totalCycles: totalCyclesValue,
    totalCyclesCustom:
      expense.totalCycles >= 13
        ? (expense.totalCycles as unknown as string)
        : undefined,
    tags: expense.tags || [],
    markAsPaid: expense.status === 'paid',
    datePaid: expense.datePaid ? new Date(expense.datePaid) : undefined,
    amountPaid: expense.amountPaid ?? undefined
  }
}
